import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { TYPES, CUSTOM, CHECKLIST_TYPE_CHECKLIST, CHECKLIST_TYPE_TEXT } from '~/Config/Checklists';
import NoScrollBar from '~/Components/Global/NoScrollBar/NoScrollBar';
import FontWrapper from '~/Containers/Global/FontWrapper/FontWrapper';
import QuillTextArea from '~/Components/Quill/QuillTextArea';
import { DATE_TIME_FORMAT, DATE_FORMAT } from '~/Redux/Constants';
import {
  allForDayAndType,
  createTask,
  updateTask,
  deleteTask,
  fetchingTasks,
} from '~/Redux/Modules/Tasks';
import TextInput from '~/Components/Global/Form/TextInput';
import CheckOpen from '~/Components/Global/Assets/Svgs/CheckOpen';
import Edit from '~/Components/Global/Assets/Svgs/Edit';
import { updateUserOptions } from '~/Redux/Modules/UserOptions';
import Spinner from '~/Components/Global/Loading/Spinner';
import TaskList from './TaskList';
import { nextPriority } from './helpers';
import TaskListHeader from './TaskListHeader';

const CustomListHeader = ({ changeListType, isText }) => {
  const customListTitle = useSelector(
    ({ UserOptions }) => UserOptions.data?.custom_list_title || 'My Custom List'
  );
  const userOptionsId = useSelector(({ UserOptions }) => UserOptions?.data?.id);
  const [editingTitle, setEditingTitle] = useState(false);
  const [title, setTitle] = useState();
  useEffect(() => {
    setTimeout(() => setTitle(customListTitle));
  }, [customListTitle]);
  const dispatch = useDispatch();
  const isFetching = useSelector(fetchingTasks).CUSTOM ?? false;

  const handleTitleSave = () => {
    setEditingTitle(false);
    dispatch(updateUserOptions(userOptionsId, { custom_list_title: title }));
  };

  const handleTitleKeyDown = ({ keyCode }) => {
    if (keyCode !== 13) return;
    handleTitleSave();
  };

  if (editingTitle) {
    return (
      <TextInput
        className="minimal-input"
        name="currentTitle"
        value={title}
        autoFocus
        onBlur={handleTitleSave}
        onKeyDown={handleTitleKeyDown}
        onChange={change => setTitle(change.currentTitle)}
      />
    );
  }

  return (
    <>
      <TaskListHeader type={CUSTOM} heading="My Custom List" editable />
      {isText ? (
        <span
          className="change-list-type__text"
          onClick={() => changeListType(CHECKLIST_TYPE_CHECKLIST)}
        >
          <CheckOpen />
        </span>
      ) : (
        <span
          className="change-list-type__checklist"
          onClick={() => changeListType(CHECKLIST_TYPE_TEXT)}
        >
          <CheckOpen />
        </span>
      )}
    </>
  );
};

CustomListHeader.propTypes = {
  changeListType: PropTypes.func.isRequired,
  isText: PropTypes.bool,
};

const CustomTaskList = ({ date, type }) => {
  const dispatch = useDispatch();
  const defaultCustomListType = useSelector(
    ({ UserOptions }) => UserOptions.data?.custom_list_type || CHECKLIST_TYPE_CHECKLIST
  );
  const userOptionsId = useSelector(({ UserOptions }) => UserOptions?.data?.id);
  const tasks = useSelector(state => allForDayAndType(state, date, type));
  const taskError = useSelector(({ Tasks }) => Tasks.updates.taskError);
  const keyTask =
    tasks.find(({ checklist_or_text }) => checklist_or_text === CHECKLIST_TYPE_TEXT) ||
    (tasks.length > 0
      ? tasks[0]
      : { checklist_or_text: defaultCustomListType, start: date.format(DATE_TIME_FORMAT), type });
  const isText = keyTask.checklist_or_text === CHECKLIST_TYPE_TEXT;
  const createId = `create-${type}-${date.format(DATE_FORMAT)}`;
  const lastUpdate = useRef();
  const lastId = useRef();
  const [changingListType, setChangingListType] = useState(false);
  const isFetching = useSelector(fetchingTasks).CUSTOM ?? false;

  useEffect(() => {
    if (keyTask?.id !== lastId.current || taskError?.id === keyTask?.id) {
      lastUpdate.current = keyTask?.summary || '';
    }
    lastId.current = keyTask?.id;
  }, [keyTask, lastUpdate, lastId, taskError]);

  const handleChangeListType = () => {
    setChangingListType(true);
    if (isText) {
      let prev;
      Promise.all([
        ...keyTask.summary
          .split('</p><p>')
          .map(line => line.replace('<p>', '').replace('</p>', ''))
          .filter(line => line && line !== '<br>')
          .map(line => {
            const next = {
              start: date.format(DATE_FORMAT),
              type,
              summary: line,
              priority: nextPriority(prev?.priority),
            };
            prev = next;
            return dispatch(createTask(next));
          }),
        keyTask?.id ? dispatch(deleteTask(keyTask)) : Promise.resolve(),
        dispatch(updateUserOptions(userOptionsId, { custom_list_type: CHECKLIST_TYPE_CHECKLIST })),
      ]).then(() => {
        setChangingListType(false);
      });
    } else {
      Promise.all([
        ...tasks.map(task => dispatch(deleteTask({ ...task, confirm: 'this' }))),
        dispatch(
          createTask({
            start: date.format(DATE_FORMAT),
            type,
            checklist_or_text: CHECKLIST_TYPE_TEXT,
            priority: nextPriority(),
            summary: tasks.map(({ summary }) => `<p>${summary || '<br>'}</p>`).join(''),
          })
        ),
        dispatch(updateUserOptions(userOptionsId, { custom_list_type: CHECKLIST_TYPE_TEXT })),
      ]).then(() => {
        setChangingListType(false);
      });
    }
  };

  const handleTextOnChange = ({ summary }) => {
    lastUpdate.current = summary;
  };

  const handleSubmitText = () => {
    if (!keyTask.id) {
      dispatch(createTask({ ...keyTask, summary: lastUpdate.current }));
    } else {
      dispatch(updateTask({ ...keyTask, summary: lastUpdate.current }));
    }
  };

  const quillTextArea = useRef();

  if (!isText)
    return (
      <TaskList
        date={date}
        type={type}
        header={<CustomListHeader changeListType={handleChangeListType} isText={isText} />}
      />
    );

  return (
    <div className="tasks-wrapper is-text-list">
      <h6 className="task-header">
        {<CustomListHeader changeListType={handleChangeListType} isText={isText} />}
      </h6>
      <FontWrapper>
        <NoScrollBar>
          {!isFetching && (
            <QuillTextArea
              id={createId}
              name="summary"
              value={lastUpdate.current || ''}
              onChange={handleTextOnChange}
              save={handleSubmitText}
              ref={quillTextArea}
              disabled={changingListType}
            />
          )}
        </NoScrollBar>
      </FontWrapper>
    </div>
  );
};

CustomTaskList.propTypes = {
  date: PropTypes.object.isRequired,
  type: PropTypes.oneOf(TYPES).isRequired,
};

CustomTaskList.defaultProps = {
  type: CUSTOM,
};

export default CustomTaskList;
